<script lang="ts" setup>
  import type { AutocompleteTypes, TextFieldTypes } from '@ionic/core'
  import { calendarClearOutline, eyeOffOutline, eyeOutline } from 'ionicons/icons'
  import type { InputCustomEvent } from '@ionic/core/dist/types/components/input/input-interface'
  import { alphaNumericRegex, alphaRegex, nameRegex, numericRegex } from '~/constants/regex'

  const props = withDefaults(
    defineProps<{
      disabled?: boolean
      errorMessage?: string
      helperText?: string
      label: string
      inputMode?: 'decimal' | 'email' | 'none' | 'numeric' | 'search' | 'tel' | 'text' | 'url'
      name: string
      maskPattern?: 'alpha' | 'alphaNumeric' | 'numeric' | 'name'
      maxLength?: number
      placeholder?: string
      readonly?: boolean
      hideLabel?: boolean
      showClearInput?: boolean
      showDatePicker?: boolean
      showCustomIcon?: boolean
      icon?: string
      type?: TextFieldTypes
      value?: string | number | null
      autocapitalize?: 'off' | 'words' | 'characters' | 'sentences'
      autocomplete?: AutocompleteTypes
    }>(),
    {
      disabled: false,
      errorMessage: undefined,
      helperText: undefined,
      inputMode: 'text',
      maskPattern: undefined,
      maxLength: 255,
      placeholder: undefined,
      readonly: false,
      hideLabel: false,
      showClearInput: false,
      showDatePicker: false,
      showCustomIcon: false,
      icon: undefined,
      type: 'text',
      value: undefined,
      autocapitalize: 'off',
      autocomplete: 'off'
    }
  )

  const emits = defineEmits<{
    (event: 'blur'): void
    (event: 'change', value: InputCustomEvent): void
    (event: 'focus'): void
    (event: 'input', value: InputCustomEvent): void
  }>()

  const isPasswordShown = ref(false)
  const inputElement = ref()

  defineExpose({ inputElement })

  const inputType = computed(() => {
    if (props.type === 'password') {
      return isPasswordShown.value ? 'text' : 'password'
    }

    return props.type
  })

  function onShowPassword() {
    isPasswordShown.value = !isPasswordShown.value
  }

  function onInput(eventHandler: 'change' | 'input', event: InputCustomEvent) {
    if (event.target.value) {
      if (typeof event.target.value === 'string') {
        const newValue = event.target.value

        switch (props.maskPattern) {
          case 'alpha':
            if (!alphaRegex.test(newValue)) {
              event.target.value = newValue.slice(0, -1)
            }
            break
          case 'alphaNumeric':
            if (!alphaNumericRegex.test(newValue)) {
              event.target.value = newValue.slice(0, -1)
            }
            break
          case 'numeric':
            if (!numericRegex.test(newValue)) {
              event.target.value = newValue.slice(0, -1)
            }
            break
          case 'name':
            if (!nameRegex.test(newValue)) {
              event.target.value = newValue.slice(0, -1)
            }
            break
          default:
            break
        }
      }
    }

    switch (eventHandler) {
      case 'change':
        emits('change', event)
        break
      case 'input':
        emits('input', event)
        break
    }
  }
</script>

<template>
  <ion-input
    ref="inputElement"
    :aria-label="label"
    :autocapitalize="autocapitalize"
    :autocomplete="autocomplete"
    :class="[{ 'ion-invalid': errorMessage }, { 'ion-touched': errorMessage }]"
    :clear-input="showClearInput"
    :disabled="disabled"
    :error-text="errorMessage"
    :helper-text="helperText"
    :inputmode="inputMode"
    :maxlength="maxLength"
    :name="name"
    :placeholder="placeholder"
    :readonly="readonly"
    :type="inputType"
    :value="value"
    class="g-input"
    label-placement="floating"
    @ion-blur="$emit('blur')"
    @ion-change="onInput('change', $event)"
    @ion-focus="$emit('focus')"
    @ion-input="onInput('input', $event)"
  >
    <span v-if="!hideLabel" slot="label" :class="['label', { 'label__error': !!errorMessage }]">{{ label }}</span>
    <!-- ion-icon quirks: Display property needs to be set with highest specificity.
      Bottom property needs to be set when input field grows in height due to error state -->
    <ion-icon
      v-if="type === 'password'"
      :icon="isPasswordShown ? eyeOutline : eyeOffOutline"
      :style="{ display: 'inline-flex !important', bottom: errorMessage || helperText ? '22px' : '0px' }"
      class="custom-icon eye-icon"
      @click="onShowPassword"
    />
    <ion-icon
      v-if="showCustomIcon"
      :icon="icon"
      :style="{ display: 'inline-flex !important', bottom: errorMessage || helperText ? '22px' : '0px' }"
      class="custom-icon"
      aria-hidden="true"
      @click="onShowPassword"
    />
    <ion-datetime-button v-if="showDatePicker && !disabled" class="date-picker" datetime="datetime">
      <span slot="date-target">
        <GIcon
          :icon="calendarClearOutline"
          :style="{
            display: 'flex !important'
          }"
        />
      </span>
    </ion-datetime-button>
  </ion-input>
</template>

<style lang="scss" scoped>
  .g-input {
    border: none;
    --padding-start: 12px;
    --padding-end: 12px;
    min-height: 50px !important;

    :deep(.input-wrapper) {
      border: 1px solid var(--color-stroke-light);
      border-radius: 4px;
    }

    :deep(input) {
      caret-color: var(--color-text-body) !important;
    }

    :deep(.label-text-wrapper) {
      caret-color: var(--color-text-body);
      transform: translateY(140%) scale(1) !important;
      transition: 0.2s all ease-out;
      height: 16px;
    }

    &.has-focus {
      > :deep(label) {
        border: 1px solid #bfc7bb !important;
      }
    }

    &.has-focus,
    &.has-value {
      :deep(.label-text-wrapper) {
        color: var(--color-text-muted) !important;
        font-size: 12px;
        height: 16px;
        transform: translateY(12px) scale(1) !important;
      }
    }

    &.ion-invalid {
      :deep(.input-wrapper) {
        border: 1px solid var(--color-alert-danger);
      }

      :deep(.label-text-wrapper) {
        color: var(--color-alert-danger);

        .label {
          color: var(--color-alert-danger) !important;
        }
      }
    }

    &.input-disabled {
      cursor: not-allowed;
      opacity: 1;

      :deep(.input-wrapper) {
        background: var(--color-neutral-grey-lightest);
        border: 1px solid var(--color-link-inverse-disabled);

        .label-text-wrapper {
          color: var(--color-text-muted);
        }

        .native-wrapper {
          color: var(--color-link-disabled);
        }
      }
    }

    // error message wrapper
    &:deep(.input-bottom) {
      border-top: none;
      padding-left: 0;

      .helper-text {
        font-size: 12px;
        user-select: none;
        color: var(--color-text-muted);
      }

      .error-text {
        font-size: 12px;
        user-select: none;
        color: var(--color-alert-danger);
      }
    }

    :deep(ion-icon) {
      display: none !important;
    }

    :deep(button) {
      width: 24px;
      height: 24px;
      margin-right: 14px;
    }
  }

  .label {
    color: var(--color-text-body) !important;
    padding-bottom: 12px;
  }

  .label__error {
    color: var(--color-base-red);
  }

  :deep(.error-message) {
    font-size: 12px;
    color: var(--color-base-red);
  }

  :deep(.input-clear-icon) {
    background-image: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGNsYXNzPSJpb25pY29uIiB2aWV3Qm94PSIwIDAgNTEyIDUxMiI+PHBhdGggZmlsbD0ibm9uZSIgc3Ryb2tlPSJjdXJyZW50Q29sb3IiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIgc3Ryb2tlLXdpZHRoPSIzMiIgZD0iTTM2OCAzNjhMMTQ0IDE0NE0zNjggMTQ0TDE0NCAzNjgiLz48L3N2Zz4=);
    top: -8px;
    position: relative;
  }

  .custom-icon {
    position: absolute;
    right: 0;
    top: 0;
    padding-right: 10px;
    margin-top: auto;
    margin-bottom: auto;
    width: 24px;
    height: 24px;
    z-index: 100;
    pointer-events: none;
    // Only apply this rule to webkit engines like Safari & Chrome.
    // Fixes Safari blurryness issue.
    // Renders fine in Chrome.
    @media screen and (-webkit-min-device-pixel-ratio: 0) {
      -webkit-transform: translate3d(0, 0, 0);
    }
  }

  .eye-icon {
    pointer-events: auto;
  }

  .date-picker {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    padding-right: 10px;
    margin-top: auto;
    margin-bottom: auto;
    z-index: 100;
    pointer-events: auto;
  }
</style>
